import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import type {BlogPostDocument, CarouselSliceFeaturePosts} from 'prismicio-types'
import type {FC} from 'react'
import {useMemo} from 'react'
import {Container} from 'src/common/Container'
import {FeaturedPostsSlider} from './components/FeaturedPostsSlider'

type FeaturedPostsProps = {
  slice: CarouselSliceFeaturePosts & {
    slice_type: string
    variation: string
  }
  pageUid: string
  componentIndex: number
  blogPosts?: BlogPostDocument<string>[]
}

export const FeaturedPosts: FC<FeaturedPostsProps> = ({slice, pageUid, componentIndex, blogPosts}) => {
  const {posts, duration, auto_populate} = slice.primary

  const resolvedPosts = useMemo(() => {
    const manualPosts = posts
      .filter((post) => isFilled.contentRelationship(post.post_document) && post.post_document.data)
      .map((post) => ({
        postDocument: post.post_document as unknown as BlogPostDocument<string>,
      }))

    if (auto_populate === '3 most recent' || auto_populate === '4 most recent') {
      const totalCount = auto_populate === '3 most recent' ? 3 : 4
      const remainingSlots = totalCount - manualPosts.length

      if (remainingSlots <= 0) {
        return manualPosts.slice(0, totalCount)
      }

      const manualIds = new Set(manualPosts.map((p) => p.postDocument.id))

      const recentPosts = (blogPosts ?? [])
        .filter((bp) => isFilled.keyText(bp.data.title) && !manualIds.has(bp.id))
        .sort((a, b) => {
          const dateA = a.data.date ?? a.first_publication_date ?? ''
          const dateB = b.data.date ?? b.first_publication_date ?? ''

          return dateB.localeCompare(dateA)
        })
        .slice(0, remainingSlots)
        .map((bp) => ({postDocument: bp}))

      return [...manualPosts, ...recentPosts]
    }

    return manualPosts
  }, [auto_populate, blogPosts, posts])

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pb-5 pt-[122px] md:!pb-0 md:!pt-[137px]"
      backgroundColor="Grey/100"
    >
      <div
        className="grid-base mx-auto gap-8 px-5 md:gap-8 md:px-10 lg:gap-12 lg:px-5 xl:px-0"
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
      >
        <div className="col-span-full flex w-full items-end justify-between">
          <PrismicRichText
            components={{
              heading1: ({children}) => (
                <h1 className="text-36 font-medium leading-normal tracking-[-0.36px] text-black lg:text-48 lg:tracking-[-0.48px]">
                  {children}
                </h1>
              ),
            }}
            field={slice.primary.title}
          />
          <PrismicRichText
            components={{
              paragraph: ({children}) => (
                <p className="text-tg-body-m font-medium text-moss-grey-400 max-md:hidden">{children}</p>
              ),
            }}
            field={slice.primary.description}
          />
        </div>

        <FeaturedPostsSlider
          posts={resolvedPosts}
          duration={duration}
          buttonLabel={slice.primary.button_label ?? undefined}
          pageUid={pageUid}
          componentIndex={componentIndex}
        />
      </div>
    </Container>
  )
}
